'use client';

import { useEffect, useCallback, useState, useRef, useMemo, startTransition } from 'react';
import { X, ArrowLeft, CaretRight, CaretLeft } from '@phosphor-icons/react';
import { CartIcon } from '@/components/icons';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import {
  useCheckoutStore,
  CHECKOUT_STEPS,
  STEP_TITLES,
  selectCurrentStep,
  selectIsCheckoutOpen,
  selectIsFirstStep,
  selectIsLastStep,
  selectContact,
  selectDeliveryDetails,
} from '@/stores/checkout-store';
// Какие шаги вообще показывать: шаг, на котором человеку нечего дать
// (например, уже полученный подарок), в воронку не попадает.
import { useVisibleCheckoutSteps } from '@/hooks/use-checkout-steps';
import { useCartStore, selectCartItems, selectCartTotal, selectMinOrderEligibleTotal, selectHasHydrated } from '@/stores/cart-store';
import { useMoneyRules, isFreeDeliveryPromoActive } from '@/lib/money-rules';
import { useAddressStore, selectDeliveryCost, selectMinOrderSum, selectAddressHasHydrated, selectSelectedAddress, selectIsInDeliveryZone } from '@/stores/address-store';
// Какие поля адреса обязательны — одно правило на корзину и на итоговый шаг.
import { validateAddressFields } from '@/lib/saved-address';
import { toast } from 'sonner';
import { ymCheckoutStep, tmrCheckoutStep } from '@/lib/analytics';
import { useCheckoutEventTracker } from '@/hooks/use-checkout-event-tracker';
import { useDeliveryRevalidate } from '@/hooks/use-delivery-revalidate';
import { useRouletteRevalidate } from '@/hooks/use-roulette-revalidate';

// Step Components
import { CartItemsStep } from './steps/cart-items-step';
import { BonusCheckStep } from './steps/bonus-check-step';
import { ExtrasStep } from './steps/extras-step';
import { DeliveryTimeStep } from './steps/delivery-time-step';
import { DeliveryDetailsStep } from './steps/delivery-details-step';
import { OrderSummaryStep } from './steps/order-summary-step';
import { PaidExtrasModal } from './paid-extras-modal';

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== MAIN COMPONENT ==========

export function CartSheet() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // ── Behavioral tracking: address_entered, phone_entered, delivery_cost_viewed ──
  useCheckoutEventTracker();
  
  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [stepKey, setStepKey] = useState(0);
  const [stepDirection, setStepDirection] = useState<'forward' | 'backward'>('forward');
  const [showPaidExtrasModal, setShowPaidExtrasModal] = useState(false);
  const prevStepRef = useRef<string | null>(null);
  
  // Checkout Store
  const isOpen = useCheckoutStore(selectIsCheckoutOpen);

  // ── Перепроверка стоимости доставки при открытии корзины ──
  // Защита от stale snapshot в localStorage: если админ изменил полигон зоны
  // или цену доставки, пока корзина была закрыта — подхватываем свежие
  // данные и показываем клиенту тост. Включается только когда корзина открыта.
  useDeliveryRevalidate({ enabled: isOpen });

  // ── Ревалидация рулеточных товаров ──
  // Если в корзине есть товар который раньше был в рулетке, а сейчас его
  // там нет (админ обновил рулетку) — удаляем + тост. По аналогии с
  // «сетом дня» — старая акция в корзине теряет валидность.
  useRouletteRevalidate({ enabled: isOpen });

  const currentStep = useCheckoutStore(selectCurrentStep);
  const isFirstStep = useCheckoutStore(selectIsFirstStep);
  const isLastStep = useCheckoutStore(selectIsLastStep);
  const closeCheckout = useCheckoutStore((state) => state.closeCheckout);
  const setStep = useCheckoutStore((state) => state.setStep);
  const extras = useCheckoutStore((state) => state.extras);

  // Видимые шаги: счётчик «Шаг X из N» и прогресс считаются по ним, иначе
  // человек видел бы «Шаг 4 из 7», пройдя четыре экрана из пяти.
  const visibleSteps = useVisibleCheckoutSteps(isOpen);
  // index = -1 ровно один кадр, пока эффект уводит с шага, который только что
  // стал невидимым. «Шаг 0 из 6» человеку показывать незачем.
  const currentStepIndex = Math.max(visibleSteps.index, 0);
  const stepProgress = visibleSteps.progress;
  const contact = useCheckoutStore(selectContact);
  const deliveryDetails = useCheckoutStore(selectDeliveryDetails);
  const setErrors = useCheckoutStore((state) => state.setErrors);
  const setShowValidationMessage = useCheckoutStore((state) => state.setShowValidationMessage);
  
  // Cart Store
  const cartHasHydrated = useCartStore(selectHasHydrated);
  const cartItems = useCartStore(selectCartItems);
  const cartTotalRaw = useCartStore(selectCartTotal);
  const minOrderEligibleRaw = useCartStore(selectMinOrderEligibleTotal);
  
  // Address Store
  const addressHasHydrated = useAddressStore(selectAddressHasHydrated);
  const deliveryCostRaw = useAddressStore(selectDeliveryCost);
  const minOrderSumRaw = useAddressStore(selectMinOrderSum);
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const isInDeliveryZone = useAddressStore(selectIsInDeliveryZone);
  
  // Checkout Store - setDeliveryDetails for address sync
  const setDeliveryDetails = useCheckoutStore((state) => state.setDeliveryDetails);
  
  // Мемоизированные вычисления
  const cartTotal = useMemo(() => cartHasHydrated ? cartTotalRaw : 0, [cartHasHydrated, cartTotalRaw]);
  // Сумма для проверки минимального заказа — без бесплатных соусов (id 1-7).
  const minOrderEligible = useMemo(() => cartHasHydrated ? minOrderEligibleRaw : 0, [cartHasHydrated, minOrderEligibleRaw]);
  const deliveryCost = useMemo(() => addressHasHydrated ? deliveryCostRaw : 0, [addressHasHydrated, deliveryCostRaw]);
  const minOrderSum = useMemo(() => addressHasHydrated ? minOrderSumRaw : 0, [addressHasHydrated, minOrderSumRaw]);
  
  // extras уже включены в cartTotal (добавлены в cart store через addItem),
  // поэтому НЕ прибавляем extrasTotal повторно
  const subtotal = useMemo(() => cartTotal, [cartTotal]);
  // Акция бесплатной доставки. Пороги — с сервера (админка «Деньги»), поэтому
  // берём их хуком: иначе экран не перерисуется после их загрузки и покажет
  // не тот порог, по которому посчитает бэк.
  const moneyRules = useMoneyRules();
  const effectiveDeliveryCost = useMemo(
    () => (isFreeDeliveryPromoActive(deliveryCost, cartTotal, moneyRules) ? 0 : deliveryCost),
    [deliveryCost, cartTotal, moneyRules]
  );
  const total = useMemo(() => subtotal + effectiveDeliveryCost, [subtotal, effectiveDeliveryCost]);
  
  // Мемоизированное форматирование цены
  const formattedTotal = useMemo(() => formatPrice(total), [total]);
  
  // Check for pending paid orders on mount — if user paid but never reached /success
  useEffect(() => {
    const checkPendingOrder = async () => {
      try {
        const saved = localStorage.getItem('lastOrderDetails');
        if (!saved) return;
        const { id } = JSON.parse(saved);
        if (!id) return;

        const res = await fetch(`/api/orders/check-payment/${id}`);
        if (!res.ok) return;
        const data = await res.json();

        if (data.payment_status === 'paid' || data.isPaid) {
          useCartStore.getState().clearCart();
          localStorage.removeItem('lastOrderDetails');
        }
      } catch { /* silently ignore */ }
    };

    checkPendingOrder();
  }, []);

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // Handle step change animation
  useEffect(() => {
    if (prevStepRef.current !== null && prevStepRef.current !== currentStep) {
      const prevIndex = CHECKOUT_STEPS.indexOf(prevStepRef.current as typeof currentStep);
      const currentIndex = CHECKOUT_STEPS.indexOf(currentStep);
      setStepDirection(currentIndex > prevIndex ? 'forward' : 'backward');
      setStepKey((prev) => prev + 1);
      // ── Яндекс.Метрика: переход на шаг оформления ──
      ymCheckoutStep(currentStep);
      tmrCheckoutStep(currentStep);
    }
    prevStepRef.current = currentStep;
  }, [currentStep]);
  
  // Global sync: keep checkout deliveryDetails.address in sync with address-store
  // This runs regardless of which step is active, so clearing address always propagates
  useEffect(() => {
    if (selectedAddress?.address && selectedAddress.isInZone) {
      setDeliveryDetails({ address: selectedAddress.address });
    } else {
      setDeliveryDetails({ address: '' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress?.address, selectedAddress?.isInZone]);
  
  // Also sync on mount (when checkout opens) to catch stale localStorage state
  useEffect(() => {
    if (isOpen && addressHasHydrated) {
      const addr = useAddressStore.getState().selectedAddress;
      if (!addr || !addr.isInZone) {
        setDeliveryDetails({ address: '' });
      } else {
        setDeliveryDetails({ address: addr.address });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, addressHasHydrated]);
  
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'hidden';
      // Block pull-to-refresh on mobile
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overscrollBehavior = 'none';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isOpen]);
  
  // Handle step navigation с startTransition
  const handleNext = useCallback(async () => {
    
    // Show paid extras reminder when leaving extras step
    if (currentStep === 'extras') {
      setShowPaidExtrasModal(true);
      return;
    }

    // Validate delivery-time step: must select date + time for scheduled
    if (currentStep === 'delivery-time') {
      const dt = useCheckoutStore.getState().deliveryTime;
      if (dt.type === 'scheduled') {
        if (!dt.selectedDate) {
          toast.error('Выберите дату доставки');
          return;
        }
        // Block past dates
        const today = new Date().toISOString().split('T')[0];
        if (dt.selectedDate < today) {
          toast.error('Нельзя выбрать прошедшую дату');
          return;
        }
        if (!dt.selectedTime) {
          toast.error('Выберите время доставки');
          return;
        }
      }
    }

    // Validate delivery-info step before proceeding
    if (currentStep === 'delivery-info') {
      const newErrors: Record<string, boolean> = {};
      
      if (!contact.name) newErrors.name = true;
      if (!contact.phone) newErrors.phone = true;
      if (!deliveryDetails.address) newErrors.address = true;
      
      if (!selectedAddress || !isInDeliveryZone) {
        newErrors.address = true;
      }
      
      // Для квартиры (не частный дом) обязательны квартира, подъезд и этаж.
      // Домофон — только если он есть: кнопка «нет домофона» снимает требование
      // (вопрос 15, ответ «В»). Раньше поле было обязательным всегда, и в доме
      // с открытым подъездом человеку оставалось поставить прочерк или уйти.
      for (const field of validateAddressFields(deliveryDetails)) {
        newErrors[field] = true;
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setShowValidationMessage(true);
        
        if (newErrors.address) {
          toast.error('Укажите полный адрес с номером дома');
        } else if (newErrors.apartment || newErrors.entrance || newErrors.floor || newErrors.intercom) {
          toast.error(
            deliveryDetails.hasIntercom
              ? 'Заполните данные квартиры: номер, подъезд, этаж, домофон'
              : 'Заполните данные квартиры: номер, подъезд, этаж'
          );
        } else if (newErrors.name && newErrors.phone) {
          toast.error('Заполните имя и телефон');
        } else if (newErrors.name) {
          toast.error('Укажите ваше имя');
        } else if (newErrors.phone) {
          toast.error('Укажите номер телефона');
        }
        return;
      }
    }
    
    const target = visibleSteps.nextVisibleStep();
    if (!target) return;
    startTransition(() => {
      setStep(target);
    });
  }, [setStep, visibleSteps, currentStep, contact, deliveryDetails, selectedAddress, isInDeliveryZone, setErrors, setShowValidationMessage]);

  const handleBack = useCallback(() => {
    const target = visibleSteps.prevVisibleStep();
    if (!target) return;
    startTransition(() => {
      setStep(target);
    });
  }, [setStep, visibleSteps]);
  
  const handleClose = useCallback(() => {
    startTransition(() => {
      closeCheckout();
    });
  }, [closeCheckout]);

  // Paid extras modal handlers
  const handlePaidExtrasGoBack = useCallback(() => {
    setShowPaidExtrasModal(false);
  }, []);

  const handlePaidExtrasContinue = useCallback(() => {
    setShowPaidExtrasModal(false);
    const target = visibleSteps.nextVisibleStep();
    if (!target) return;
    startTransition(() => {
      setStep(target);
    });
  }, [setStep, visibleSteps]);

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'cart':
        return <CartItemsStep onNext={handleNext} />;
      case 'bonus':
        return <BonusCheckStep onNext={handleNext} onBack={handleBack} />;
      case 'extras':
        return <ExtrasStep onNext={handleNext} onBack={handleBack} />;
      case 'delivery-time':
        return <DeliveryTimeStep onNext={handleNext} onBack={handleBack} />;
      case 'delivery-info':
        return <DeliveryDetailsStep onNext={handleNext} onBack={handleBack} />;
      case 'summary':
        return <OrderSummaryStep onBack={handleBack} />;
      default:
        return <CartItemsStep onNext={handleNext} />;
    }
  };
  
  // Мемоизированные вычисления для кнопки
  const buttonText = useMemo(() => {
    if (isLastStep) return 'Оплатить';
    if (currentStep === 'cart') {
      if (cartItems.length === 0) return 'Корзина пуста';
      if (minOrderSum > 0 && minOrderEligible < minOrderSum) {
        const remaining = minOrderSum - minOrderEligible;
        return `Ещё ${formatPrice(remaining)}`;
      }
    }
    return 'Далее';
  }, [isLastStep, currentStep, cartItems.length, minOrderSum, minOrderEligible]);
  
  const canProceed = useMemo(() => {
    if (currentStep === 'cart') {
      if (cartItems.length === 0) return false;
      if (minOrderSum > 0 && minOrderEligible < minOrderSum) return false;
      return true;
    }
    return true;
  }, [currentStep, cartItems.length, minOrderSum, minOrderEligible]);
  
  if (!isVisible) return null;
  
  return (
    <div className="fixed inset-0 z-[9000]">
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        onClick={closeCheckout}
      />
      
      {/* Sheet */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0',
          'w-full sm:w-[450px]',
          'flex flex-col',
          'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
          isDark ? 'bg-[var(--bg-primary)]' : 'bg-white',
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between gap-3 px-4 py-3 border-b',
            'border-[var(--border-default)]'
          )}
          style={{ touchAction: 'none' }}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2">
            {!isFirstStep ? (
              <button
                onClick={handleBack}
                className={cn(
                  'flex items-center justify-center w-9 h-9 rounded-pill',
                  'transition-[background-color,opacity] duration-100 active:scale-[0.98] active:transition-none',
                  isDark 
                    ? 'bg-[var(--surface-secondary)] text-[var(--text-primary)]' 
                    : 'bg-[var(--violet-50)] text-[var(--primary)]'
                )}
              >
                <ArrowLeft weight="bold" className="w-4 h-4" />
              </button>
            ) : (
              <div className={cn(
                'flex items-center justify-center w-9 h-9 rounded-pill',
                isDark
                  ? 'bg-[var(--surface-secondary)] text-[var(--primary)]' 
                  : 'bg-[var(--violet-50)] text-[var(--primary)]'
              )}>
                <CartIcon weight="fill" className="w-4 h-4" />
              </div>
            )}
            
            <div>
              <h2 className={cn(
                'text-base font-semibold',
                'text-[var(--text-primary)]'
              )}>
                {STEP_TITLES[currentStep]}
              </h2>
              {!isFirstStep && (
                <p className={cn(
                  'text-xs',
                  'text-[var(--text-tertiary)]'
                )}>
                  Шаг {currentStepIndex + 1} из {visibleSteps.steps.length}
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={handleClose}
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-pill',
              'transition-[background-color,opacity] duration-100 active:scale-[0.98] active:transition-none',
              isDark 
                ? 'hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]' 
                : 'hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]'
            )}
          >
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className={cn(
          'flex-1 relative overscroll-contain overflow-y-auto'
        )}>
          <div
            key={stepKey}
            className={cn(
              'h-full',
              stepDirection === 'forward' ? 'animate-step-enter' : 'animate-step-enter',
            )}
          >
            {renderStepContent()}
          </div>
        </div>
        
        {/* Footer — hidden during summary step */}
        {cartItems.length > 0 && currentStep !== 'summary' && (
          <div
            className={cn(
              'px-4 py-4 border-t',
              'border-[var(--border-default)]'
            )}
            style={{ touchAction: 'none' }}
            onTouchMove={(e) => e.preventDefault()}
          >
            
            {/* Total & Buttons */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className={cn(
                  'text-xs block mb-0.5',
                  'text-[var(--text-tertiary)]'
                )}>
                  Итого к оплате:
                </span>
                <span className={cn(
                  'text-xl font-bold font-mono',
                  'text-[var(--text-primary)]'
                )}>
                  {formatPrice(total)}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <button
                    onClick={handleBack}
                    className={cn(
                      'h-12 px-4 rounded-card border font-medium text-sm flex items-center gap-1',
                      'transition-[background-color,opacity] duration-100 active:scale-[0.98] active:transition-none',
                      isDark 
                        ? 'border-[var(--border-default)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]' 
                        : 'border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--violet-50)]'
                    )}
                  >
                    <CaretLeft weight="bold" className="w-4 h-4" />
                    Назад
                  </button>
                )}
                
                <button
                  onClick={handleNext}
                  disabled={!canProceed}
                  className={cn(
                    'h-12 px-6 rounded-card font-medium text-sm flex items-center gap-1 transition-[background-color,opacity,box-shadow] duration-100',
                    'active:scale-[0.98]',
                    canProceed
                      ? 'bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)] hover:shadow-float'
                      : 'bg-[var(--surface-disabled)] text-[var(--text-disabled)]'
                  )}
                >
                  {buttonText}
                  {!isLastStep && <CaretRight weight="bold" className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className={cn(
              'mt-3 h-1 rounded-hairline overflow-hidden',
              isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--violet-50)]'
            )}>
              <div
                className="h-full bg-[var(--primary)] transition-all duration-300 ease-out"
                style={{ width: `${stepProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Paid extras reminder modal */}
      <PaidExtrasModal
        isOpen={showPaidExtrasModal}
        onGoBack={handlePaidExtrasGoBack}
        onContinue={handlePaidExtrasContinue}
      />
    </div>
  );
}
